import { Controller } from '@hotwired/stimulus';

// Mirrors Priceable::ENGINE_DRIVEN_VERSIONS on the backend. Covers all engine
// families (v2, v2.1, v3.0 through v3.5) that need the plan-toggle UI bootstrapped.
const ENGINE_DRIVEN_VERSIONS = ['v2', 'v2.1', 'v3.0', 'v3.1', 'v3.2', 'v3.3', 'v3.4', 'v3.5'];
const isEngineDriven = (version) => ENGINE_DRIVEN_VERSIONS.includes(version);

export default class extends Controller {
  static targets = [
    'objectiveField', 'intervalField',
    'v2SkuField',
    'computedSku', 'orderPrice',
    'frequencyOptions', 'recipeOption',
    'intervalSection',
  ];

  static values = {
    version: String,
    plans: Object,
    halfPlans: Object,
    currentRecipe: String,
    initialPlanType: String,
  };

  connect() {
    this.currentPlanType = this.initialPlanTypeValue || 'full';
    this.rememberedPlans = {};
    this._boundOnPlanTypeChanged = this._onPlanTypeChanged.bind(this);
    window.addEventListener('account-plan-type:changed', this._boundOnPlanTypeChanged);

    if (isEngineDriven(this.versionValue)) {
      this.renderFrequencyOptions(this.currentRecipeValue);
      this.updateComputedOutputs();
    }
  }

  disconnect() {
    window.removeEventListener('account-plan-type:changed', this._boundOnPlanTypeChanged);
  }

  _onPlanTypeChanged(event) {
    const planType = event.detail?.planType;
    if (!planType || planType === this.currentPlanType) return;
    if (!isEngineDriven(this.versionValue)) return;

    this._rememberSelectedPlan(this.currentPlanType, this.currentRecipeValue);
    this.currentPlanType = planType;
    this.renderFrequencyOptions(this.currentRecipeValue);
    this.updateComputedOutputs();
  }

  _rememberKey(planType, recipe) {
    return `${planType}:${recipe}`;
  }

  _rememberSelectedPlan(planType, recipe) {
    if (!planType || !recipe || !this.hasIntervalFieldTarget) return;
    const interval = parseInt(this.intervalFieldTarget.value);
    if (Number.isNaN(interval)) return;
    this.rememberedPlans[this._rememberKey(planType, recipe)] = {
      interval_count: interval,
      objective: this.hasObjectiveFieldTarget ? this.objectiveFieldTarget.value : null,
    };
  }

  _recallSelectedPlan(planType, recipe) {
    return this.rememberedPlans[this._rememberKey(planType, recipe)];
  }

  _plansSource() {
    if (
      this.currentPlanType === 'half' &&
      this.hasHalfPlansValue &&
      (this.halfPlansValue?.[this.currentRecipeValue] || []).length > 0
    ) {
      return this.halfPlansValue;
    }
    return this.plansValue;
  }

  selectRecipe(e) {
    const recipe = e.currentTarget.dataset.recipe;
    if (recipe === this.currentRecipeValue) return;

    this._rememberSelectedPlan(this.currentPlanType, this.currentRecipeValue);
    this.currentRecipeValue = recipe;

    this.recipeOptionTargets.forEach((option) => {
      const isSelected = option.dataset.recipe === recipe;
      option.classList.toggle('price-engine-toggle__recipe-option--selected', isSelected);
      const radio = option.querySelector('input[type="radio"]');
      if (radio) radio.checked = isSelected;
    });

    this.renderFrequencyOptions(recipe);
    this.updateComputedOutputs();
  }

  selectFrequency(e) {
    const interval = parseInt(e.currentTarget.dataset.interval);
    const plans = this._plansSource()[this.currentRecipeValue] || [];
    const plan = plans.find((p) => p.interval_count === interval);
    if (!plan) return;

    this.applyPlan(plan);

    e.currentTarget
      .closest('[data-price-engine-toggle-target="frequencyOptions"]')
      ?.querySelectorAll('[data-interval]')
      .forEach((opt) => {
        const isSelected = parseInt(opt.dataset.interval) === interval;
        opt.classList.toggle('price-engine-toggle__frequency-option--selected', isSelected);
        const radio = opt.querySelector('input[type="radio"]');
        if (radio) radio.checked = isSelected;
      });

    this.updateComputedOutputs(plan);
  }

  applyPlan(plan) {
    if (this.hasIntervalFieldTarget) {
      this.intervalFieldTarget.value = plan.interval_count;
    }
    this.objectiveFieldTarget.value = plan.objective;
    if (this.hasV2SkuFieldTarget) {
      this.v2SkuFieldTarget.value = plan.sku_id;
    }
  }

  updateComputedOutputs(plan = null) {
    const resolvedPlan = plan || this.currentPlan();

    if (this.hasComputedSkuTarget) {
      this.computedSkuTarget.textContent = resolvedPlan?.sku_name || '—';
    }
    if (this.hasOrderPriceTarget) {
      this.orderPriceTarget.textContent = resolvedPlan?.price_per_order_cents
        ? (resolvedPlan.price_per_order_cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
        : '—';
    }
  }

  currentPlan() {
    const plans = this._plansSource()[this.currentRecipeValue] || [];
    if (plans.length === 0) return null;

    const interval = this.hasIntervalFieldTarget
      ? parseInt(this.intervalFieldTarget.value)
      : null;
    return plans.find((p) => p.interval_count === interval) || plans[0];
  }

  _resolveSelectedPlan(plans, recipe) {
    if (plans.length === 0) return null;

    const remembered = this._recallSelectedPlan(this.currentPlanType, recipe);
    if (remembered) {
      const match =
        plans.find((p) => p.interval_count === remembered.interval_count && p.objective === remembered.objective) ||
        plans.find((p) => p.interval_count === remembered.interval_count);
      if (match) return match;
    }

    const currentInterval = this.hasIntervalFieldTarget ? parseInt(this.intervalFieldTarget.value) : null;
    return plans.find((p) => p.interval_count === currentInterval) || plans[0];
  }

  renderFrequencyOptions(recipe) {
    if (!this.hasFrequencyOptionsTarget) return;

    const plans = this._plansSource()[recipe] || [];

    if (plans.length === 0) {
      this.frequencyOptionsTarget.innerHTML =
        '<p class="c-text-sm monument-regular text-muted fst-italic">Plan data unavailable for this dog profile.</p>';
      return;
    }

    const selectedPlan = this._resolveSelectedPlan(plans, recipe);

    this.frequencyOptionsTarget.innerHTML = plans
      .map((plan) => {
        const isSelected = plan.interval_count === selectedPlan.interval_count;
        const selectedClass = isSelected ? ' price-engine-toggle__frequency-option--selected' : '';
        const pricePerDay = (plan.price_per_day_cents / 100).toFixed(2);
        const bestValueBadge = plan.best_value
          ? '<span class="badge text-bg-success monument-regular c-text-xs text-uppercase">Best Value</span>'
          : '';

        return `
          <label class="d-flex align-items-center rounded border p-3 price-engine-toggle__frequency-option${selectedClass}"
                 data-action="click->price-engine-toggle#selectFrequency"
                 data-interval="${plan.interval_count}">
            <input type="radio" name="v2_frequency" value="${plan.interval_count}"
                   ${isSelected ? 'checked' : ''} class="me-3">
            <div class="d-flex flex-column flex-grow-1">
              <span class="monument-regular fw-bold">Every ${plan.interval_count} days</span>
              <span class="c-text-sm monument-regular text-muted">$${pricePerDay} / day</span>
            </div>
            ${bestValueBadge}
          </label>
        `;
      })
      .join('');

    this.applyPlan(selectedPlan);
    this.updateComputedOutputs(selectedPlan);
  }
}
